<template>
    <div>
        <b-row class="mb-3 align-items-end">
            <b-col md="4">
                <b-form-group label="업체 ID 필터 (선택)" class="mb-0">
                    <b-form-input v-model="companyFilter" type="number" placeholder="예: 34090"
                                  :disabled="loading" @keyup.enter="load"
                    />
                </b-form-group>
            </b-col>
            <b-col md="3">
                <b-button variant="outline-primary" :disabled="loading" @click="load">
                    <b-spinner v-if="loading" small class="mr-1"/>
                    조회
                </b-button>
                <b-button v-if="companyFilter" variant="link" :disabled="loading" @click="clearFilter">
                    필터 해제
                </b-button>
            </b-col>
        </b-row>

        <b-table :items="logs" :fields="fields" :busy="loading" small striped responsive
                 show-empty empty-text="실행 이력이 없습니다."
        >
            <template #table-busy>
                <div class="text-center py-3">
                    <b-spinner small class="mr-1"/>불러오는 중...
                </div>
            </template>
            <template #cell(status)="row">
                <b-badge :variant="row.value === 'EXECUTED' ? 'success' : 'danger'">
                    {{ row.value }}
                </b-badge>
            </template>
            <template #cell(campaign)="row">
                {{ row.item.from_campaign_id }} &rarr; {{ row.item.to_campaign_id }}
            </template>
            <template #cell(created_at)="row">
                {{ formatDate(row.value) }}
            </template>
            <template #cell(actions)="row">
                <b-button size="sm" variant="outline-secondary" @click="openDetail(row.item)">
                    백업 보기
                </b-button>
            </template>
        </b-table>

        <!-- ── 백업 상세 ── -->
        <b-modal v-model="detailOpen" size="xl" title="삭제된 원본 (before-image)"
                 ok-only ok-title="닫기" scrollable
        >
            <div v-if="detailLoading" class="text-center py-4">
                <b-spinner small class="mr-1"/>불러오는 중...
            </div>
            <div v-else-if="detailError">
                <b-alert variant="danger" show>{{ detailError }}</b-alert>
            </div>
            <div v-else-if="detail">
                <b-alert variant="info" show class="py-2">
                    <i class="bi bi-info-circle mr-1"></i>
                    복구는 자동으로 되지 않습니다. 아래 내용을 근거로 담당자가 직접 복원해야 합니다.
                </b-alert>

                <b-row class="mb-3 small">
                    <b-col md="3">업체: <strong>{{ detail.company_id }}</strong></b-col>
                    <b-col md="3">
                        캠페인: <strong>{{ detail.from_campaign_id }} &rarr; {{ detail.to_campaign_id }}</strong>
                    </b-col>
                    <b-col md="3">실행자: <strong>{{ detail.created_by }}</strong></b-col>
                    <b-col md="3">{{ formatDate(detail.created_at) }}</b-col>
                </b-row>

                <b-row class="text-center mb-3">
                    <b-col v-for="(v, k) in detail.backup_counts" :key="k">
                        <div class="count-box">
                            <div class="count-label">{{ countLabel(k) }}</div>
                            <div class="count-value">{{ v }}</div>
                        </div>
                    </b-col>
                </b-row>

                <b-tabs content-class="mt-3" small>
                    <b-tab :title="`삭제된 답변 (${backupSubs.length})`">
                        <b-table v-if="backupSubs.length" :items="backupSubs" :fields="subFields"
                                 small striped responsive sticky-header="420px"
                        >
                            <template #cell(sub_data_contents)="row">
                                <code class="small">{{ truncate(row.value) }}</code>
                            </template>
                        </b-table>
                        <p v-else class="text-muted mb-0">삭제된 답변이 없습니다.</p>
                    </b-tab>
                    <b-tab :title="`삭제된 감사자 평가 (${backupEvals.length})`">
                        <b-table v-if="backupEvals.length" :items="backupEvals" :fields="evalFields"
                                 small striped responsive sticky-header="420px"
                        />
                        <p v-else class="text-muted mb-0">삭제된 평가가 없습니다.</p>
                    </b-tab>
                    <b-tab :title="`삭제된 첨부 (${backupAttachments.length})`">
                        <b-table v-if="backupAttachments.length" :items="backupAttachments"
                                 :fields="attachmentFields" small striped responsive sticky-header="420px"
                        />
                        <p v-else class="text-muted mb-0">삭제된 첨부가 없습니다.</p>
                    </b-tab>
                    <b-tab title="실행 요약">
                        <pre class="summary-json">{{ prettySummary }}</pre>
                    </b-tab>
                </b-tabs>
            </div>
        </b-modal>
    </div>
</template>

<script>
/**
 * 답변 보존 캠페인 재배정 — 실행 이력 / 백업 회수 탭.
 *
 * 복구 기능은 없다(수동 SQL). 이 화면이 삭제된 원본을 꺼내는 유일한 경로이며,
 * 여기 내용을 근거로 담당자가 복원하거나 "무엇이 지워졌는지"를 확인한다.
 *
 * 목록은 요약만 받고(backups=false 상당), 백업 전문은 상세를 열 때만 받는다 —
 * 삭제 답변이 많으면 응답이 수백 KB 라 목록에 싣기엔 무겁다.
 */
import {
    fetchRemapLogDetail,
    fetchRemapLogs,
    remapErrorMessage,
} from '@shared_utils/api/campaignRemap';

export default {
    name: 'RemapHistoryTab',
    props: {
        /** 실행 탭에서 넘어올 때 바로 열 이력 id (lazy 탭 ref 타이밍 회피용 prop). */
        autoOpenLogId: {type: Number, default: null},
    },
    data() {
        return {
            logs: [],
            loading: false,
            companyFilter: '',
            detailOpen: false,
            detailLoading: false,
            detailError: '',
            detail: null,
            fields: [
                {key: 'id', label: '#'},
                {key: 'company_id', label: '업체 ID'},
                {key: 'campaign', label: '캠페인 변경'},
                {key: 'status', label: '상태'},
                {key: 'created_by', label: '실행자'},
                {key: 'created_at', label: '실행 시각'},
                {key: 'actions', label: ''},
            ],
            subFields: [
                {key: 'id', label: 'ID'},
                {key: 'sub_code', label: '문항 코드'},
                {key: 'question_code', label: '질문 코드'},
                {key: 'sub_data_contents', label: '답변 내용'},
            ],
            evalFields: [
                {key: 'id', label: 'ID'},
                {key: 'ev_id', label: '평가요소'},
                {key: 'satisfied', label: '충족'},
                {key: 'author_id', label: '평가자'},
                {key: 'comment', label: '코멘트'},
            ],
            attachmentFields: [
                {key: 'id', label: 'ID'},
                {key: 'filename', label: '파일명'},
                {key: 's3_key', label: 'S3 키'},
            ],
        };
    },
    computed: {
        backupSubs() {
            return (this.detail && this.detail.backups && this.detail.backups.deleted_subs) || [];
        },
        backupEvals() {
            return (this.detail && this.detail.backups
                && this.detail.backups.deleted_evaluations) || [];
        },
        backupAttachments() {
            return (this.detail && this.detail.backups
                && this.detail.backups.deleted_attachment_storage) || [];
        },
        prettySummary() {
            if (!this.detail) return '';
            return JSON.stringify(this.detail.result_summary, null, 2);
        },
    },
    async mounted() {
        await this.load();
        if (this.autoOpenLogId) {
            const found = this.logs.find(l => l.id === this.autoOpenLogId);
            if (found) this.openDetail(found);
            this.$emit('auto-opened');
        }
    },
    methods: {
        async load() {
            this.loading = true;
            try {
                this.logs = await fetchRemapLogs({
                    companyId: this.companyFilter ? Number(this.companyFilter) : null,
                });
            } catch (e) {
                this.$EventBus.$emit(
                    'toast-failure', '조회 오류',
                    remapErrorMessage(e, '실행 이력을 불러오지 못했습니다.'),
                );
            } finally {
                this.loading = false;
            }
        },
        clearFilter() {
            this.companyFilter = '';
            this.load();
        },
        async openDetail(item) {
            this.detailOpen = true;
            this.detailLoading = true;
            this.detailError = '';
            this.detail = null;
            try {
                this.detail = await fetchRemapLogDetail(item.id);
            } catch (e) {
                this.detailError = remapErrorMessage(e, '백업을 불러오지 못했습니다.');
            } finally {
                this.detailLoading = false;
            }
        },
        countLabel(key) {
            return {
                deleted_subs: '답변',
                deleted_reviews: '검토의견',
                deleted_evaluations: '감사자 평가',
                deleted_attachment_storage: '첨부',
                deleted_sub_attachments: '첨부 링크',
            }[key] || key;
        },
        truncate(value, len = 120) {
            if (value === null || value === undefined) return '';
            const s = typeof value === 'string' ? value : JSON.stringify(value);
            return s.length > len ? `${s.slice(0, len)}...` : s;
        },
        formatDate(iso) {
            if (!iso) return '';
            return String(iso).replace('T', ' ').slice(0, 19);
        },
    },
};
</script>

<style scoped>
.count-box {
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 8px;
}

.count-label {
    font-size: 0.75rem;
    color: #6c757d;
}

.count-value {
    font-size: 1.25rem;
    font-weight: 600;
}

.summary-json {
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 12px;
    max-height: 420px;
    overflow: auto;
    font-size: 0.8rem;
}
</style>
